// DescuentosTab — SERIE «Descuentos», T2 sub-fases 2a (crear + previsualizar) y
// 2b (APLICAR). Consume los endpoints de JOB de T1 (POST previsualizar/aplicar →
// jobId, GET .../previsualizar/:jobId poll — el registro de jobs es uno solo).
// Progreso REAL (contado/total), nunca una barra girando sola. El aplicar RELEE
// fresco en el backend (diff-y-para): si un precio cambió en Woo desde la
// previsualización, NO se escribe nada y las diferencias se muestran acá.
// Textos en tuteo, genéricos multi-tienda.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from './ui/Button';
import { MENSAJE_SOLO_LECTURA } from '../utils/soloLectura';
import './DescuentosTab.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POLL_MS = 2000;
const MARGEN_LABEL = 'Margen s/ precio de venta (IVA incl.)';

const REDONDEOS = [
  { v: 'centena_abajo', t: 'Centena hacia abajo' },
  { v: 'millar_abajo', t: 'Millar hacia abajo' },
  { v: 'termina_en_900', t: 'Termina en 900' },
  { v: 'sin_redondeo', t: 'Sin redondeo' },
];

const money = (n) => (n == null ? '—' : Number(n).toLocaleString('es-CO'));

// Etiquetas humanas de los motivos de no-aplicación (para el desglose del resumen).
const MOTIVO_LABEL = {
  ya_cubierta: 'ya cubiertas',
  bajo_piso_margen: 'bajo el piso de margen',
  precio_cero_o_negativo: 'precio en cero o negativo',
};
// "Ya cubierta" NO es un problema: es `respetar` funcionando. Neutro, no rojo.
const esMotivoNeutro = (motivo) => motivo === 'ya_cubierta';

// Rótulo de la oferta vigente por fila (registrada o "manual").
function ofertaLabel(ov) {
  if (!ov) return '—';
  if (ov.origen === 'manual') return 'en oferta (manual, sin registro)';
  if (ov.origen === 'campana') return `campaña${ov.nombre ? ` «${ov.nombre}»` : ''}`;
  if (ov.origen === 'descuento') return `descuento${ov.nombre ? ` «${ov.nombre}»` : ''}`;
  if (ov.origen === 'promocion') return 'promoción suelta';
  return 'en oferta';
}

export default function DescuentosTab({ soloLectura = false }) {
  const [categorias, setCategorias] = useState([]);
  const [modo, setModo] = useState('categoria'); // 'categoria' | 'catalogo'
  const [selCat, setSelCat] = useState(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [productos, setProductos] = useState([]);
  const [selProd, setSelProd] = useState(new Set());
  const [pegarModo, setPegarModo] = useState(false); // entrada avanzada dentro de "catálogo"
  const [pegado, setPegado] = useState('');

  const [nombre, setNombre] = useState('');
  const [tipoRegla, setTipoRegla] = useState('porcentaje'); // 'porcentaje' | 'monto'
  const [valor, setValor] = useState('');
  const [base, setBase] = useState('regular'); // 'regular' | 'actual'
  const [redondeo, setRedondeo] = useState('centena_abajo');
  const [pisoMargen, setPisoMargen] = useState('');
  const [yaEnOferta, setYaEnOferta] = useState('respetar'); // 'respetar' | 'sobrescribir'
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Estado del JOB de previsualización.
  const [jobEstado, setJobEstado] = useState(null); // null | 'corriendo' | 'listo' | 'error'
  const [progreso, setProgreso] = useState({ hechas: 0, total: 0 });
  const [estimado, setEstimado] = useState(null); // { productos, llamadas, segundos }
  const [preview, setPreview] = useState(null);
  const [coberturaCero, setCoberturaCero] = useState(null);
  const [vencida, setVencida] = useState(null);
  const [leidoHace, setLeidoHace] = useState(null); // segundos
  const [fuente, setFuente] = useState(null); // 'cache' | 'lectura'
  const [error, setError] = useState('');
  const [previewJobId, setPreviewJobId] = useState(null); // el job cuya lectura respalda el Aplicar

  // Estado del APLICAR (2b): confirmación en dos clics + job de escritura + diff-y-para.
  const [confirmando, setConfirmando] = useState(false);
  const [aplicarEstado, setAplicarEstado] = useState(null); // null | 'aplicando' | 'listo' | 'error'
  const [aplicarProgreso, setAplicarProgreso] = useState({ hechas: 0, total: 0 }); // ESCRITURAS (lotes)
  const [aplicarEstimado, setAplicarEstimado] = useState(null); // { lotes, llamadas, segundos }
  const [aplicarInfo, setAplicarInfo] = useState(null); // { nombre, a_escribir, no_aplicadas } del POST
  const [aplicarResultado, setAplicarResultado] = useState(null); // { actualizadas, fallidas }
  const [aplicarError, setAplicarError] = useState('');
  const [diferencias, setDiferencias] = useState(null); // diff-y-para: qué cambió en Woo

  // ── HISTORIAL UNIFICADO (2c): lista + detalle SOLO MONGO + revertir ruteado ──
  const [historial, setHistorial] = useState(null);
  const [histError, setHistError] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [detalleCargando, setDetalleCargando] = useState(null); // `${origen}:${id}` en lectura
  const [detalleError, setDetalleError] = useState('');
  const detalleRef = useRef(null);
  const [revCheck, setRevCheck] = useState(null); // chequeo pre-reversión (la única lectura a Woo)
  const [revComprobando, setRevComprobando] = useState(false);
  const [revEjecutando, setRevEjecutando] = useState(false);
  const [revResultado, setRevResultado] = useState(null); // { restauradas, fallidas }
  const [revError, setRevError] = useState('');

  const pollRef = useRef(null);
  const pollAplicarRef = useRef(null);
  const detener = useCallback(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);
  const detenerAplicar = useCallback(() => { if (pollAplicarRef.current) { clearInterval(pollAplicarRef.current); pollAplicarRef.current = null; } }, []);
  useEffect(() => detener, [detener]); // limpiar el poll al desmontar
  useEffect(() => detenerAplicar, [detenerAplicar]);

  const cargarCategorias = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/promociones/categorias`);
      const d = await r.json();
      if (r.ok) setCategorias(d.categorias || []);
    } catch (_) { /* el picker no bloquea */ }
  }, []);
  useEffect(() => { cargarCategorias(); }, [cargarCategorias]);

  const toggle = (set, val) => { const s = new Set(set); if (s.has(val)) s.delete(val); else s.add(val); return s; };

  const criterioActual = () => {
    if (modo === 'categoria') return { tipo: 'categoria', valores: [...selCat] };
    if (pegarModo) return { tipo: 'productos', valores: pegado.split(/[\s,]+/).map(Number).filter(Number.isFinite) };
    return { tipo: 'productos', valores: [...selProd] };
  };
  const reglaActual = () => ({
    tipo: tipoRegla, valor: Number(valor), base, redondeo,
    pisoMargen: pisoMargen === '' ? null : Number(pisoMargen), yaEnOferta,
  });

  // Estado del aplicar a cero (lo comparten previsualizar y limpiar: una preview
  // nueva invalida la confirmación, las diferencias y el resultado anteriores).
  const resetAplicar = () => {
    detenerAplicar();
    setConfirmando(false); setAplicarEstado(null); setAplicarProgreso({ hechas: 0, total: 0 });
    setAplicarEstimado(null); setAplicarInfo(null); setAplicarResultado(null); setAplicarError(''); setDiferencias(null);
  };

  const limpiar = () => {
    detener();
    resetAplicar();
    setJobEstado(null); setPreview(null); setCoberturaCero(null); setVencida(null); setError(''); setPreviewJobId(null);
    setProgreso({ hechas: 0, total: 0 }); setEstimado(null); setLeidoHace(null); setFuente(null);
    setSelCat(new Set()); setSelProd(new Set()); setPegado(''); setNombre(''); setValor(''); setPisoMargen('');
  };

  const buscarProductos = async () => {
    try {
      const r = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(busqueda)}&status=publish&per_page=20`);
      const d = await r.json();
      if (r.ok) setProductos((d.products || []).filter((p) => p.type === 'variable' || p.type === 'simple'));
    } catch (_) { /* no bloquea */ }
  };

  const consultar = useCallback(async (jobId) => {
    try {
      const r = await fetch(`${API_URL}/api/descuentos/previsualizar/${jobId}`);
      const d = await r.json();
      if (!r.ok) { detener(); setJobEstado('error'); setError(d.error || 'Se perdió la previsualización. Vuelve a intentar.'); return; }
      setProgreso(d.progreso || { hechas: 0, total: 0 });
      if (d.estimado) setEstimado(d.estimado);
      if (d.estado === 'error') { detener(); setJobEstado('error'); setError(d.error || 'No se pudo previsualizar.'); return; }
      if (d.estado === 'listo') {
        detener();
        const res = d.resultado || {};
        if (res.vencida) { setVencida(res); setJobEstado(null); return; }
        if (res.cobertura_cero) { setCoberturaCero(res); setJobEstado(null); return; }
        setPreview(res); setFuente(res.fuente || null); setLeidoHace(d.leido_hace_seg != null ? d.leido_hace_seg : null);
        setJobEstado('listo');
      }
    } catch (e) { detener(); setJobEstado('error'); setError(e.message); }
  }, [detener]);

  const previsualizar = useCallback(async (releer = false) => {
    detener();
    resetAplicar(); // una preview nueva invalida confirmación/diferencias/resultado viejos
    setError(''); setPreview(null); setCoberturaCero(null); setVencida(null); setPreviewJobId(null);
    setProgreso({ hechas: 0, total: 0 }); setEstimado(null); setJobEstado('corriendo');
    try {
      const r = await fetch(`${API_URL}/api/descuentos/previsualizar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regla: reglaActual(), ventana: { fechaInicio: fechaInicio || null, fechaFin: fechaFin || null }, criterio: criterioActual(), releer }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo iniciar la previsualización');
      setPreviewJobId(d.jobId); // el aplicar presenta este job para el diff contra la lectura previa
      pollRef.current = setInterval(() => consultar(d.jobId), POLL_MS);
      consultar(d.jobId); // primer poll inmediato
    } catch (e) { setJobEstado('error'); setError(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detener, consultar, tipoRegla, valor, base, redondeo, pisoMargen, yaEnOferta, fechaInicio, fechaFin, modo, selCat, selProd, pegarModo, pegado]);

  // ── HISTORIAL (2c): la carga vive ANTES del aplicar porque el aplicar la
  // refresca al terminar (sin esto, el descuento recién aplicado no aparece en
  // la lista hasta recargar la página — lo cazó el e2e de 2c).
  const cargarHistorial = useCallback(async () => {
    setHistError('');
    try {
      const r = await fetch(`${API_URL}/api/descuentos/historial`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
      setHistorial(d);
    } catch (e) { setHistError(`No se pudo cargar el historial: ${e.message}. Prueba de nuevo.`); }
  }, []);
  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  // ── APLICAR (2b) ──────────────────────────────────────────────────────────
  const consultarAplicar = useCallback(async (jobId) => {
    try {
      const r = await fetch(`${API_URL}/api/descuentos/previsualizar/${jobId}`);
      const d = await r.json();
      if (!r.ok) {
        detenerAplicar(); setAplicarEstado('error');
        setAplicarError(d.error || 'Se perdió el seguimiento de la aplicación. Revisa el historial para ver qué quedó escrito.');
        return;
      }
      setAplicarProgreso(d.progreso || { hechas: 0, total: 0 });
      if (d.estado === 'error') { detenerAplicar(); setAplicarEstado('error'); setAplicarError(d.error || 'No se pudo aplicar.'); return; }
      if (d.estado === 'listo') {
        detenerAplicar();
        setAplicarResultado(d.resultado || { actualizadas: [], fallidas: [] });
        setAplicarEstado('listo');
        cargarHistorial(); // el descuento recién aplicado tiene que aparecer en la lista
      }
    } catch (e) { detenerAplicar(); setAplicarEstado('error'); setAplicarError(e.message); }
  }, [detenerAplicar, cargarHistorial]);

  const aplicar = async () => {
    if (!confirmando) { setConfirmando(true); return; } // 1.er clic: solo abre la confirmación
    setConfirmando(false); setDiferencias(null); setAplicarError(''); setAplicarResultado(null); setAplicarInfo(null);
    setAplicarProgreso({ hechas: 0, total: 0 }); setAplicarEstado('aplicando');
    try {
      const origen = modo === 'categoria' ? 'categoria' : (pegarModo ? 'pegado' : 'catalogo');
      const r = await fetch(`${API_URL}/api/descuentos/aplicar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre, origen, regla: reglaActual(),
          ventana: { fechaInicio: fechaInicio || null, fechaFin: fechaFin || null },
          criterio: criterioActual(), previewJobId,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setAplicarEstado(null);
        // Diff-y-para: el backend RELEYÓ fresco y algo cambió → no escribió nada.
        if (d.diferencias && d.diferencias.length) { setDiferencias(d.diferencias); return; }
        setAplicarEstado('error'); setAplicarError(d.error || 'No se pudo aplicar el descuento.');
        return;
      }
      setAplicarInfo({ nombre: d.nombre, a_escribir: d.a_escribir, no_aplicadas: d.no_aplicadas || [] });
      if (d.estimado) setAplicarEstimado(d.estimado);
      pollAplicarRef.current = setInterval(() => consultarAplicar(d.jobId), POLL_MS);
      consultarAplicar(d.jobId); // primer poll inmediato
    } catch (e) { setAplicarEstado('error'); setAplicarError(e.message); }
  };

  const ritmoPorMin = estimado && estimado.segundos ? Math.round((estimado.llamadas * 60) / estimado.segundos) : null;

  // ── HISTORIAL (2c) — cargarHistorial vive arriba (el aplicar la refresca) ──
  // La tarjeta del detalle se agrega DEBAJO de la lista: sin esto el clic parece
  // no hacer nada (misma lección que campanas-ver-detalle). jsdom no trae scrollIntoView.
  useEffect(() => {
    if (detalle && detalleRef.current && typeof detalleRef.current.scrollIntoView === 'function') {
      detalleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [detalle]);

  const limpiarReversion = () => { setRevCheck(null); setRevComprobando(false); setRevEjecutando(false); setRevResultado(null); setRevError(''); };

  // Detalle SOLO MONGO: nunca lee Woo (un detalle que lee la tienda por variación
  // es lento y parece colgado — aprendido de Campañas). Woo se lee al revertir.
  const verDetalle = async (origen, id) => {
    if (detalleCargando) return; // ya hay una lectura en curso
    setDetalle(null); setDetalleError(''); limpiarReversion();
    setDetalleCargando(`${origen}:${id}`);
    try {
      const r = await fetch(`${API_URL}/api/descuentos/historial/${origen}/${id}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
      setDetalle(d);
    } catch (e) {
      setDetalleError(`No se pudo cargar el detalle: ${e.message}. Prueba de nuevo.`);
    } finally {
      setDetalleCargando(null);
    }
  };

  // 1.er clic de Revertir: comprueba la tienda (la ÚNICA lectura a Woo del
  // historial) para saber qué vuelve y qué fue cambiado a mano (eso NO se pisa).
  const prepararRevertir = async () => {
    if (revComprobando || revEjecutando) return;
    setRevError(''); setRevResultado(null); setRevCheck(null); setRevComprobando(true);
    try {
      const r = await fetch(`${API_URL}/api/descuentos/historial/${detalle.origen}/${detalle.id}/reversion`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
      setRevCheck(d);
    } catch (e) {
      setRevError(`No se pudo comprobar la tienda antes de revertir: ${e.message}`);
    } finally {
      setRevComprobando(false);
    }
  };

  // 2.º clic: ejecuta RUTEADO POR ORIGEN — cada tipo por su propio endpoint
  // (no se reimplementa lógica). Las modificadas a mano quedan afuera del body.
  const RUTA_REVERTIR = {
    descuento: (id) => `/api/descuentos/${id}/revertir`,
    campana: (id) => `/api/promociones/campanas/${id}/quitar`,
    promocion_suelta: (id) => `/api/promociones/${id}/revertir`,
  };
  const ejecutarRevertir = async () => {
    if (!revCheck || revEjecutando) return;
    setRevEjecutando(true); setRevError('');
    try {
      const r = await fetch(`${API_URL}${RUTA_REVERTIR[detalle.origen](detalle.id)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variacionesARestaurar: revCheck.restaurables }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `el servidor respondió ${r.status}`);
      // Cada endpoint nombra distinto lo restaurado (restauradas / actualizadas).
      setRevResultado({ restauradas: d.restauradas || d.actualizadas || [], fallidas: d.fallidas || [] });
      setRevCheck(null);
      setDetalle((prev) => (prev ? { ...prev, revertible: false } : prev));
      cargarHistorial(); // el estado de la fila cambió
    } catch (e) {
      setRevError(`No se pudo revertir: ${e.message}`);
    } finally {
      setRevEjecutando(false);
    }
  };

  const ORIGEN_LABEL = { descuento: 'Descuento', campana: 'Campaña', promocion_suelta: 'Promoción suelta' };
  const fechaCorta = (f) => (f ? String(f).slice(0, 10) : '—');

  // El resultado guardado, en lenguaje del dueño: «264 previsualizadas · 90
  // aplicadas · 0 fallidas · 174 excluidas (ya en oferta, en otra campaña)».
  // «90 escritas» sobre una campaña que cubre 90 confundía (revisión de Miguel,
  // 21-sep). El paréntesis lista SOLO los motivos que el doc realmente trae;
  // sin motivos guardados (docs viejos) queda «174 excluidas» pelado.
  const MOTIVO_EXCLUIDA = {
    ya_cubierta: 'ya en oferta', ya_tenia_oferta: 'ya en oferta',
    sube_el_precio_vigente: 'subiría el precio', la_campana_sube_el_precio_actual: 'subiría el precio',
    precio_cero_o_negativo: 'precio en cero', precio_de_promocion_en_cero_o_negativo: 'precio en cero',
    en_otra_campana_activa: 'en otra campaña', bajo_piso_margen: 'bajo el piso de margen',
    sin_regular_price: 'sin precio regular',
  };
  const resumenResultado = (r) => {
    if (!r) return '';
    const a = (r.actualizadas || []).length;
    const f = (r.fallidas || []).length;
    const n = (r.no_aplicadas || []).length;
    const motivos = [...new Set((r.no_aplicadas || []).map((x) => x && x.motivo).filter(Boolean).map((m) => MOTIVO_EXCLUIDA[m] || m))];
    const parentetico = n > 0 && motivos.length ? ` (${motivos.join(', ')})` : '';
    const s = (x, sg, pl) => `${x} ${x === 1 ? sg : pl}`;
    return ` · ${s(a + f + n, 'previsualizada', 'previsualizadas')} · ${s(a, 'aplicada', 'aplicadas')} · ${s(f, 'falló', 'fallidas')} · ${s(n, 'excluida', 'excluidas')}${parentetico}`;
  };

  // Totales SOLO sobre lo que tiene costo (margen no nulo); las sin costo quedan afuera.
  const filas = preview?.filas || [];
  const conCosto = filas.filter((f) => f.margen_pct != null);
  const sinCosto = filas.length - conCosto.length;
  const margenProm = conCosto.length ? Math.round((conCosto.reduce((s, f) => s + f.margen_pct, 0) / conCosto.length) * 10) / 10 : null;

  // Lo que el Aplicar va a cambiar (para la confirmación del 2.º clic).
  const aplicables = filas.filter((f) => f.aplicable);
  const productosACambiar = [...new Set(aplicables.map((f) => f.producto).filter(Boolean))];
  // Sin costo el piso de margen no tiene contra qué comparar: esas variaciones se
  // escriben SIN esa protección, y la confirmación lo dice (solo si existen).
  const sinCostoACambiar = aplicables.filter((f) => f.margen_pct == null).length;
  // El botón solo se habilita con previsualización HECHA y nombre PUESTO.
  const puedeAplicar = jobEstado === 'listo' && !!preview && nombre.trim() !== '' && aplicarEstado !== 'aplicando';

  // Precio de una diferencia ('' = sin oferta; los precios del diff viajan como string).
  const precioDiff = (s) => (s == null || s === '' ? '—' : money(Number(s)));
  const TIPO_DIFF = { 'cambió': 'cambió el precio', 'apareció': 'apareció (no estaba al previsualizar)', 'desapareció': 'ya no está en la selección' };

  return (
    <div className="desc-tab">
      {/* ── Crear ─────────────────────────────────────────────────────────── */}
      <section className="desc-card">
        <h3>Crear descuento</h3>

        <div className="desc-modo" role="group" aria-label="De dónde salen los productos">
          <Button variant={modo === 'categoria' ? 'primary' : 'secondary'} size="sm" onClick={() => setModo('categoria')}>Por categoría</Button>
          <Button variant={modo === 'catalogo' ? 'primary' : 'secondary'} size="sm" onClick={() => setModo('catalogo')}>Desde el catálogo</Button>
        </div>

        {modo === 'categoria' ? (
          <div className="desc-picker" aria-label="Categorías">
            {categorias.length === 0 ? <p className="desc-hint">Cargando categorías…</p>
              : categorias.map((c) => (
                <label key={c.id} className="desc-check">
                  <input type="checkbox" checked={selCat.has(c.id)} onChange={() => setSelCat((s) => toggle(s, c.id))} />
                  {c.name} ({c.count})
                </label>
              ))}
            <p className="desc-hint">La selección es por id. El alcance real lo confirma la previsualización.</p>
          </div>
        ) : (
          <div className="desc-picker">
            <label className="desc-check desc-avanzada">
              <input type="checkbox" checked={pegarModo} onChange={(e) => setPegarModo(e.target.checked)} />
              Pegar una lista de ids (entrada avanzada)
            </label>
            {pegarModo ? (
              <textarea
                aria-label="Ids de productos, separados por coma o espacio" className="desc-pegar"
                value={pegado} onChange={(e) => setPegado(e.target.value)}
                placeholder="Ej: 10841, 10644, 10195"
              />
            ) : (
              <>
                <div className="desc-buscar">
                  <input aria-label="Buscar producto" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre del producto" />
                  <Button size="sm" onClick={buscarProductos}>Buscar</Button>
                </div>
                {productos.map((p) => (
                  <label key={p.id} className="desc-check">
                    <input type="checkbox" checked={selProd.has(p.id)} onChange={() => setSelProd((s) => toggle(s, p.id))} />
                    {p.name}
                  </label>
                ))}
              </>
            )}
          </div>
        )}

        {/* Regla */}
        <div className="desc-params">
          <label>Nombre<input aria-label="Nombre del descuento" value={nombre} onChange={(e) => setNombre(e.target.value)} /></label>
          <label>Tipo
            <select aria-label="Tipo de descuento" value={tipoRegla} onChange={(e) => setTipoRegla(e.target.value)}>
              <option value="porcentaje">Porcentaje (%)</option>
              <option value="monto">Monto ($)</option>
            </select>
          </label>
          <label>{tipoRegla === 'porcentaje' ? 'Valor (%)' : 'Valor ($)'}<input aria-label="Valor del descuento" type="number" value={valor} onChange={(e) => setValor(e.target.value)} /></label>
          <label>Sobre
            <select aria-label="Base de cálculo" value={base} onChange={(e) => setBase(e.target.value)}>
              <option value="regular">Precio regular</option>
              <option value="actual">Precio actual (oferta vigente)</option>
            </select>
          </label>
          <label>Redondeo
            <select aria-label="Redondeo" value={redondeo} onChange={(e) => setRedondeo(e.target.value)}>
              {REDONDEOS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </label>
          <label>Piso de margen (%)<input aria-label="Piso de margen en porcentaje" type="number" value={pisoMargen} onChange={(e) => setPisoMargen(e.target.value)} placeholder="opcional" /></label>
          <label>Ya en oferta
            <select aria-label="Qué hacer con lo que ya está en oferta" value={yaEnOferta} onChange={(e) => setYaEnOferta(e.target.value)}>
              <option value="respetar">Respetar (no tocar)</option>
              <option value="sobrescribir">Sobrescribir</option>
            </select>
          </label>
          <label>Desde<input aria-label="Fecha de inicio" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} /></label>
          <label>Hasta<input aria-label="Fecha de fin" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} /></label>
        </div>
        <p className="desc-hint">Sin fechas, la oferta queda activa sin vencimiento. El piso de margen es sobre el precio de venta (IVA incl.); bloquea lo que quedaría por debajo.</p>

        {error && <p className="desc-error" role="alert">{error}</p>}

        <div className="desc-actions">
          <Button onClick={() => previsualizar(false)} loading={jobEstado === 'corriendo'} disabled={jobEstado === 'corriendo' || aplicarEstado === 'aplicando'}>Previsualizar</Button>
          {(preview || vencida || coberturaCero || jobEstado === 'error') && <Button variant="secondary" onClick={limpiar}>Empezar de nuevo</Button>}
        </div>

        {/* ── Progreso REAL (nunca una barra girando sola) ─────────────────── */}
        {jobEstado === 'corriendo' && (
          <p className="desc-progreso" role="status" aria-live="polite">
            {(!progreso.total)
              ? 'Buscando productos…'
              : <>Leyendo <strong>{progreso.hechas} / {progreso.total}</strong> productos…
                {estimado ? ` ~${estimado.segundos}s` : ''}{ritmoPorMin ? ` · ritmo controlado ${ritmoPorMin}/min` : ''}</>}
          </p>
        )}
        {jobEstado === 'error' && (
          <p className="desc-error" role="alert">
            Falló tras leer {progreso.hechas}/{progreso.total || '—'}: {error}
          </p>
        )}
        {vencida && (
          <div className="desc-vencida" role="status">
            {vencida.motivo || `Los precios se leyeron hace ${vencida.leido_hace_min} min y vencieron.`}
            <div className="desc-actions"><Button size="sm" onClick={() => previsualizar(true)}>Releer precios</Button></div>
          </div>
        )}
        {coberturaCero && <p className="desc-hint" role="status">{coberturaCero.motivo}</p>}

        {/* ── Resumen + tabla ──────────────────────────────────────────────── */}
        {preview && (
          <>
            <p className="desc-leido" role="status">
              {fuente === 'cache' ? 'Precios del caché' : 'Precios leídos'}{leidoHace != null ? ` hace ${leidoHace < 60 ? `${leidoHace}s` : `${Math.round(leidoHace / 60)} min`}` : ''}.
            </p>
            <div className="desc-resumen">
              <span>{preview.total} variaciones · {preview.aplicables} aplicables</span>
              {preview.subirian_de_precio > 0 && <span className="desc-sube" role="alert">⚠️ {preview.subirian_de_precio} subirían de precio</span>}
              {preview.no_aplicadas > 0 && (
                <span>
                  {preview.no_aplicadas} no aplicables
                  {preview.no_aplicadas_por_motivo && Object.keys(preview.no_aplicadas_por_motivo).length > 0 && (
                    <> ({Object.entries(preview.no_aplicadas_por_motivo).map(([m, n]) => `${n} ${MOTIVO_LABEL[m] || m}`).join(' · ')})</>
                  )}
                </span>
              )}
              {preview.margen_no_verificable > 0 && <span>{preview.margen_no_verificable} sin costo</span>}
            </div>
            {conCosto.length > 0 && (
              <p className="desc-totales">
                Margen promedio (solo con costo): <strong>{margenProm}%</strong> — costo estimado (sin respaldo de factura).
                {sinCosto > 0 && ` ${sinCosto} sin costo, fuera del total.`}
                {reglaActual().pisoMargen != null && ` Piso de margen ${reglaActual().pisoMargen}% (costo estimado, sin factura).`}
              </p>
            )}

            <div className="desc-tabla" role="table" aria-label="Previsualización del descuento">
              <div className="desc-fila desc-fila--head" role="row">
                <span>Producto</span><span>Variación</span><span>Actual</span><span>Regular</span>
                <span>Promoción</span><span>% real</span><span>{MARGEN_LABEL}</span><span>Oferta vigente</span><span>Estado</span>
              </div>
              {filas.map((f, i) => (
                <div key={`${f.variation_id}-${i}`} className={`desc-fila${!f.aplicable ? (esMotivoNeutro(f.no_aplicada?.motivo) ? ' desc-fila--neutra' : ' desc-fila--no') : ''}`} role="row">
                  <span>{f.producto || `#${f.variation_id}`}</span>
                  <span>{f.variacion || '—'}</span>
                  <span>{money(f.precio_actual)}</span>
                  <span>{money(f.precio_regular)}</span>
                  <span>{money(f.precio_promocion)}</span>
                  <span>{f.pct_real != null ? `${f.pct_real}%` : '—'}</span>
                  <span>
                    {f.margen_pct == null
                      ? <em className="desc-sincosto">sin costo</em>
                      : <>{f.margen_pct}% <span className="desc-estimado">(costo estimado, sin factura)</span></>}
                  </span>
                  <span>{ofertaLabel(f.oferta_vigente)}</span>
                  <span>
                    {f.no_aplicada
                      ? (esMotivoNeutro(f.no_aplicada.motivo)
                        ? <span className="desc-neutro">No se aplicará: {f.no_aplicada.detalle || f.no_aplicada.motivo}</span>
                        : <strong className="desc-no">No se aplicará: {f.no_aplicada.detalle || f.no_aplicada.motivo}</strong>)
                      : (f.sube_el_precio_vigente ? <span className="desc-sube">⚠️ sube el precio vigente</span> : 'Aplicable')}
                    {f.alertas && f.alertas.includes('por_debajo_del_costo') && <span className="desc-alerta"> · bajo el costo</span>}
                    {f.alertas && f.alertas.includes('margen_no_verificable') && <span className="desc-alerta"> · margen no verificable</span>}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── APLICAR (2b) ─────────────────────────────────────────────────── */}
        <div className="desc-aplicar">
          {/* El aviso de subidas va ANTES del botón y NO dentro de la tabla:
              imposible de pasar por alto. Solo aparece si el número existe
              (un aviso en cero enseña a ignorar los avisos). */}
          {preview && preview.subirian_de_precio > 0 && aplicarEstado !== 'listo' && (
            <div className="desc-aviso-sube" role="alert">
              ⚠️ Atención: {preview.subirian_de_precio} {preview.subirian_de_precio === 1 ? 'variación SUBIRÍA' : 'variaciones SUBIRÍAN'} de precio si aplicas este descuento.
            </div>
          )}

          {confirmando && (
            <p className="desc-confirmar" role="status">
              Se van a cambiar <strong>{aplicables.length} {aplicables.length === 1 ? 'variación' : 'variaciones'}</strong> de{' '}
              {productosACambiar.length} {productosACambiar.length === 1 ? 'producto' : 'productos'}: {productosACambiar.join(', ')}.
              {sinCostoACambiar > 0 && (
                <span className="desc-confirmar-sincosto">
                  {sinCostoACambiar} de estas variaciones no {sinCostoACambiar === 1 ? 'tiene' : 'tienen'} costo registrado: el piso de margen no {sinCostoACambiar === 1 ? 'la' : 'las'} protege.
                </span>
              )}
            </p>
          )}

          <div className="desc-actions">
            <Button
              variant={confirmando ? 'destructive' : 'primary'}
              onClick={aplicar}
              disabled={!puedeAplicar || soloLectura}
              title={soloLectura ? MENSAJE_SOLO_LECTURA : undefined}
              loading={aplicarEstado === 'aplicando'}
            >
              {confirmando ? '¿Seguro? Aplicar descuento' : 'Aplicar descuento'}
            </Button>
            {confirmando && <Button variant="secondary" onClick={() => setConfirmando(false)}>Cancelar</Button>}
          </div>
          {preview && jobEstado === 'listo' && !nombre.trim() && (
            <p className="desc-hint">Ponle un nombre al descuento para poder aplicarlo.</p>
          )}

          {/* Diff-y-para: el backend releyó fresco, algo cambió → NO se escribió nada. */}
          {diferencias && (
            <div className="desc-diferencias" role="alert">
              <p className="desc-diferencias-titulo">
                Los precios cambiaron en la tienda desde la previsualización: <strong>no se escribió nada</strong>.
              </p>
              <div className="desc-tabla" role="table" aria-label="Precios que cambiaron desde la previsualización">
                <div className="desc-fila desc-fila--diff desc-fila--head" role="row">
                  <span>Variación</span><span>Qué pasó</span><span>Al previsualizar</span><span>Ahora en la tienda</span>
                </div>
                {diferencias.map((df) => (
                  <div key={df.variation_id} className="desc-fila desc-fila--diff" role="row">
                    <span>#{df.variation_id}</span>
                    <span>{TIPO_DIFF[df.tipo] || df.tipo}</span>
                    <span>{df.antes ? <>regular {precioDiff(df.antes.regular_price)} · oferta {precioDiff(df.antes.sale_price)}</> : '—'}</span>
                    <span>{df.ahora ? <>regular {precioDiff(df.ahora.regular_price)} · oferta {precioDiff(df.ahora.sale_price)}</> : '—'}</span>
                  </div>
                ))}
              </div>
              <div className="desc-actions">
                <Button size="sm" onClick={() => previsualizar(true)}>Volver a previsualizar</Button>
              </div>
            </div>
          )}

          {/* Progreso REAL de la escritura: contado, nunca una barra sola. */}
          {aplicarEstado === 'aplicando' && (
            <p className="desc-progreso" role="status" aria-live="polite">
              Aplicando <strong>{`${aplicarProgreso.hechas} / ${aplicarProgreso.total || (aplicarEstimado ? aplicarEstimado.lotes : '—')}`}</strong> escrituras…
              {aplicarEstimado ? ` ~${aplicarEstimado.segundos}s · ritmo controlado` : ''}
            </p>
          )}

          {/* Resultado: cuántas se aplicaron, cuántas fallaron y por qué. */}
          {aplicarEstado === 'listo' && aplicarResultado && (
            <div className="desc-resultado" role="status">
              <p>
                <strong>Descuento «{(aplicarInfo && aplicarInfo.nombre) || nombre}» aplicado.</strong>{' '}
                {(aplicarResultado.actualizadas || []).length} {(aplicarResultado.actualizadas || []).length === 1 ? 'variación actualizada' : 'variaciones actualizadas'}
                {' · '}{(aplicarResultado.fallidas || []).length} {(aplicarResultado.fallidas || []).length === 1 ? 'falló' : 'fallaron'}
                {aplicarInfo && aplicarInfo.no_aplicadas.length > 0 ? ` · ${aplicarInfo.no_aplicadas.length} no aplicadas (previstas en la previsualización)` : ''}.
              </p>
              {(aplicarResultado.fallidas || []).length > 0 && (
                <ul className="desc-fallidas">
                  {aplicarResultado.fallidas.map((f) => (
                    <li key={f.variation_id}>Variación #{f.variation_id}: {f.error || 'error de WooCommerce'}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Error con CAUSA (IP bloqueada, 429 agotado, Woo caído), igual que en 2a. */}
          {aplicarEstado === 'error' && (
            <p className="desc-error" role="alert">
              {aplicarProgreso.total > 0
                ? `Falló tras ${aplicarProgreso.hechas}/${aplicarProgreso.total} escrituras: ${aplicarError} Revisa el historial para ver qué alcanzó a escribirse.`
                : `No se pudo aplicar: ${aplicarError}`}
            </p>
          )}
        </div>
      </section>

      {/* ── HISTORIAL UNIFICADO (2c): las tres colecciones en una lista ────── */}
      <section className="desc-card">
        <h3>Historial</h3>
        {histError && <p className="desc-error" role="alert">{histError}</p>}
        {historial === null && !histError ? <p className="desc-hint">Cargando historial…</p>
          : historial && (historial.filas || []).length === 0 ? <p className="desc-hint">Todavía no hay descuentos, campañas ni promociones aplicadas.</p>
            : historial && (
              <div className="desc-tabla" role="table" aria-label="Historial de descuentos, campañas y promociones">
                <div className="desc-fila desc-fila--hist desc-fila--head" role="row">
                  <span>Origen</span><span>Nombre</span><span>Regla</span><span>Fecha</span><span>Variaciones</span><span>Estado</span><span />
                </div>
                {historial.filas.map((f) => (
                  <div key={`${f.origen}-${f.id}`} className="desc-fila desc-fila--hist" role="row">
                    <span>{ORIGEN_LABEL[f.origen] || f.origen}</span>
                    <span>{f.nombre || '—'}</span>
                    <span>{f.regla || '—'}</span>
                    <span>{fechaCorta(f.fecha)}</span>
                    <span>{f.cobertura}</span>
                    <span>{(f.estado && f.estado.etiqueta) || '—'}</span>
                    <span>
                      <Button size="sm" aria-label={`Ver detalle de ${f.nombre || `${ORIGEN_LABEL[f.origen] || f.origen} del ${fechaCorta(f.fecha)}`}`}
                        disabled={detalleCargando !== null} onClick={() => verDetalle(f.origen, f.id)}>
                        {detalleCargando === `${f.origen}:${f.id}` ? 'Cargando…' : 'Ver detalle'}
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
            )}
        {detalleError && <p className="desc-error" role="alert">{detalleError}</p>}
      </section>

      {/* ── Detalle (solo Mongo) + revertir ruteado por origen ─────────────── */}
      {detalle && (
        <section ref={detalleRef} className="desc-card" aria-label={`Detalle de ${detalle.nombre || ORIGEN_LABEL[detalle.origen] || 'la fila'}`}>
          <h3>{detalle.nombre || ORIGEN_LABEL[detalle.origen]} <span className="desc-estado">({(detalle.estado && detalle.estado.etiqueta) || '—'})</span></h3>
          <p className="desc-hint">
            {ORIGEN_LABEL[detalle.origen]}{detalle.regla ? ` · ${detalle.regla}` : ''} · aplicada el {fechaCorta(detalle.fecha)}
            {detalle.ventana && (detalle.ventana.fechaInicio || detalle.ventana.fechaFin)
              ? ` · ventana ${detalle.ventana.fechaInicio || 'abierta'}${detalle.ventana.fechaFin ? ` → ${detalle.ventana.fechaFin}` : ' (sin vencimiento)'}` : ''}
            {detalle.autor ? ` · por ${detalle.autor}` : ''}
          </p>
          <p>
            Cubre <strong>{detalle.cobertura}</strong> {detalle.cobertura === 1 ? 'variación' : 'variaciones'} · {detalle.previos_total} con valor anterior guardado
            {resumenResultado(detalle.resultado)}.
          </p>
          {detalle.revertidaEn && <p className="desc-hint">Revertida el {fechaCorta(detalle.revertidaEn)}{detalle.revertidaPor ? ` por ${detalle.revertidaPor}` : ''}.</p>}

          {detalle.revertible && !revResultado && (
            <div className="desc-actions">
              <Button variant="destructive" onClick={prepararRevertir} loading={revComprobando} disabled={revComprobando || revEjecutando || !!revCheck}>Revertir</Button>
            </div>
          )}
          {revComprobando && (
            <p className="desc-progreso" role="status" aria-live="polite">Comprobando en la tienda si algo cambió después de aplicar…</p>
          )}

          {/* Confirmación (2.º clic): qué vuelve y qué NO se pisa. */}
          {revCheck && !revResultado && (
            <div className="desc-confirmar" role="status">
              <p>
                Van a volver a su precio anterior <strong>{revCheck.restaurables.length} {revCheck.restaurables.length === 1 ? 'variación' : 'variaciones'}</strong>
                {revCheck.restaurables.length !== revCheck.total_previos ? ` de las ${revCheck.total_previos} con valor guardado` : ''}.
              </p>
              {revCheck.modificadas_por_fuera.length > 0 && (
                <div className="desc-rev-modificadas">
                  <p className="desc-alerta">
                    {revCheck.modificadas_por_fuera.length} {revCheck.modificadas_por_fuera.length === 1 ? 'fue cambiada' : 'fueron cambiadas'} a mano después de aplicar — no se tocan, quedan como están:
                  </p>
                  <ul>
                    {revCheck.modificadas_por_fuera.map((m) => (
                      <li key={m.variation_id}>
                        #{m.variation_id}: se escribió oferta {precioDiff(m.escrito && m.escrito.sale_price)} · ahora tiene oferta {precioDiff(m.actual && m.actual.sale_price)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {revCheck.restaurables.length === 0 && (
                <p className="desc-hint">Todas fueron cambiadas a mano después de aplicar: no hay nada que revertir sin pisar esos cambios.</p>
              )}
              <div className="desc-actions">
                <Button variant="destructive" onClick={ejecutarRevertir} loading={revEjecutando} disabled={revEjecutando || revCheck.restaurables.length === 0}>¿Seguro? Revertir</Button>
                <Button variant="secondary" onClick={limpiarReversion} disabled={revEjecutando}>Cancelar</Button>
              </div>
              {revEjecutando && (
                <p className="desc-progreso" role="status" aria-live="polite">
                  Revirtiendo {revCheck.restaurables.length} {revCheck.restaurables.length === 1 ? 'variación' : 'variaciones'}… (ritmo controlado a la tienda: puede tardar unos minutos)
                </p>
              )}
            </div>
          )}

          {revResultado && (
            <div className="desc-resultado" role="status">
              <p>
                <strong>Reversión hecha.</strong> {revResultado.restauradas.length} {revResultado.restauradas.length === 1 ? 'variación volvió' : 'variaciones volvieron'} a su precio anterior
                {' · '}{revResultado.fallidas.length} {revResultado.fallidas.length === 1 ? 'falló' : 'fallaron'}.
              </p>
              {revResultado.fallidas.length > 0 && (
                <ul className="desc-fallidas">
                  {revResultado.fallidas.map((f) => <li key={f.variation_id}>Variación #{f.variation_id}: {f.error || 'error de WooCommerce'}</li>)}
                </ul>
              )}
            </div>
          )}
          {revError && <p className="desc-error" role="alert">{revError}</p>}
        </section>
      )}
    </div>
  );
}
